
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { DashboardFinanceiroInfos } from "../../domain/models/dashboard_financeiro_infos";
import { DashboardFinanceiroChart } from "../../domain/models/dashboard_financeiro_chart";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { FluxoCaixaForm } from "../schemas/Principal_financeiro/Fluxo_caixa_form";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import CommonConstants from "../../../../common/constants";
import { usePrincipalFinanceiroDocPagination } from "../hooks/principal_financeiro/pagination";
import { initialContasPagarFiltersValues, useContasPagarListaFilters } from "../hooks/contas_pagar_lista/filters";
import { initialContasReceberFiltersValues, useContasReceberListaFilters } from "../hooks/contas_receber_lista/filters";

/**
 * usePrincipalFinanceiroViewModel
 */

type LoadPrincipalListaFuncState = {
    IdArquivoModulo?: number;
    page?: number;
    rowsPerPage?: number;
    tipoPesquisa?: number,
    pesquisa?: string,
};

export const usePrincipalFinanceiroViewModel = () => {

    const {
        reports: {
            getReportFluxoCaixaUseCase,
        },
        commons: {
            getAllListagemDocUseCase,
            getGedArquivoDataUseCase,
            getCentroCustoDropDownUseCase,
            getFornecedorDropDownUseCase,
            getClientesDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
        },
        financeiro: {
            getDashboardFinanceiroInfosUseCaseUseCase,
            getDashboardFinanceiroChartUseCaseUseCase,
        },

    } = useDependencyInjection();

    const backOrNavigate = useBackOrNavigate();
    const pagination = usePrincipalFinanceiroDocPagination();

    const { pushErrorNotification } = usePageLoader();


    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [reportViewerState, startReportViewerProcess] = useReportViewer();
    const [openRelatorioModal, setOpenRelatorioModal] = useState(false);

    const [filtroAno, setFiltroAno] = useState<number>(new Date().getFullYear());

    const [dashboardFinanceiroInformation, setDashboardFinanceiroInformation] = useState<DashboardFinanceiroInfos>();
    const [dashboardFinanceiroChart, setDashboardFinanceiroChart] = useState<DashboardFinanceiroChart[]>();

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<DropDown[]>();

    const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

    const filtersContasPagar = useContasPagarListaFilters();
    const filtersContasReceber = useContasReceberListaFilters();

    const cardIds = useMemo(() => {
        return {
            contasPagarAtraso: 1,
            contasReceberAtraso: 2
        };
    }, [
    ]);

    /**
     * Funções de carregamento
     */


    //#region CARREGAMENTO DROPDOWNS.

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setLoading(false);
        }

    }, [
        getCentroCustoDropDownUseCase,
        pushErrorNotification,
    ]);



    const loadBancoDropDown = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setLoading(false);
        }
    }, [
        getBancosPagamentoDropDownUseCase,
        pushErrorNotification
    ]);


    const loadFornecedorDropDown = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(
                result.map((x) => ({
                    id: x.idFornecedor,
                    title: x.nomeFantasia
                }))
            );

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar fornecedores!'));
        } finally {
            setLoading(false);
        }
    }, [getFornecedorDropDownUseCase,
        pushErrorNotification
    ]);

    const loadClienteDropDown = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setLoading(false);
        }
    }, [
        getClientesDropDownUseCase,
        pushErrorNotification
    ]);


    //#endregion





    //#region CARREGAMENTO 

    const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
        try {
            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllListagemDocUseCase.getAllListagemDoc(
                String(CommonConstants.moduloDocumentos.Financeiro),
                filteredPage,
                filteredRowsPerPage
            );
            setDocLista(getAllListagemDocUseCase.items);
            pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
            pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar documentos!");
        }
    }, [getAllListagemDocUseCase, pagination]);


    const loadDashboardFinanceiroChart = useCallback(async (ano?: number, mes?: number) => {
        try {
            setLoading(true);
            const chartResponse = await getDashboardFinanceiroChartUseCaseUseCase.execute(ano ?? filtroAno, mes);
            setDashboardFinanceiroChart(chartResponse);

        } catch (e: unknown) {
            setDashboardFinanceiroChart(undefined);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [
        getDashboardFinanceiroChartUseCaseUseCase,
        filtroAno,
    ])

    const loadDashboardFinanceiroInfo = useCallback(async (ano?: number) => {
        try {
            setLoading(true);
            const response = await getDashboardFinanceiroInfosUseCaseUseCase.execute();
            setDashboardFinanceiroInformation(response);

        } catch (e: unknown) {
            setDashboardFinanceiroInformation(undefined);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [
        getDashboardFinanceiroInfosUseCaseUseCase,
    ]);



    //#endregion


    /**
     * useEffect
     */

    usePageStartBootstrap(async () => {
        try {
            await Promise.all([
                loadDashboardFinanceiroChart(),
                loadDashboardFinanceiroInfo(),
                loadCentroCustoDropDown(),
                loadBancoDropDown(),
                loadFornecedorDropDown(),
                loadClienteDropDown(),
                loadDocListaState(),
            ]);

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao carregar os dados!")[0]);
        }
    });

    /**
     * Eventos
     */

    const handleReload = useCallback(() => {
        try {
            setLoading(true);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao recarregar os dados!"));
        } finally {
            setLoading(false);
        }
    }, [
        pushErrorNotification,
    ]);

    const handleCardClick = useCallback((cardId: number) => {
        switch (cardId) {
            case cardIds.contasPagarAtraso:
                filtersContasPagar.setFilters({ ...initialContasPagarFiltersValues, status: 3 });
                backOrNavigate("/ContasPagar_lista");
                break;
            case cardIds.contasReceberAtraso:
                filtersContasReceber.setFilters({ ...initialContasReceberFiltersValues, status: 3 });
                backOrNavigate("/ContasReceber_lista");
                break;
        }
    }, [
        cardIds,
        filtersContasPagar,
        filtersContasReceber,
        backOrNavigate,
    ]);

    const handleAnoChange = useCallback(async (ano: number) => {
        setFiltroAno(ano);
        try {
            setLoading(true);
            await loadDashboardFinanceiroChart(ano, undefined);

        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
        } finally {
            setLoading(false);
        }
    },
        [loadDashboardFinanceiroChart, pushErrorNotification]
    );

    const handleMesChange = useCallback(async (mes?: number) => {
        try {
            setLoading(true);
            await loadDashboardFinanceiroChart(undefined, mes);

        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar contas a pagar!"));
        } finally {
            setLoading(false);
        }
    },
        [loadDashboardFinanceiroChart, pushErrorNotification]
    );

    // #region TABELA DOCUMENTOS

    const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
        if (IdArquivoModulo > 0) {
            try {
                setLoading(true);
                await getGedArquivoDataUseCase
                    .getGedArquivoData(IdArquivoModulo, NomeArquivo);
            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
            } finally {
                setLoading(false);
            }
        }
    }, [getGedArquivoDataUseCase, pushErrorNotification]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);;
            await loadDocListaState({ page: page + 1, });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
        } finally {
            setLoading(false);
        }
    }, [loadDocListaState, pagination, pushErrorNotification]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);

            await loadDocListaState({ page: 1, rowsPerPage });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
        } finally {
            setLoading(false);
        }
    }, [loadDocListaState, pagination, pushErrorNotification]);

    //#endregion




    //#region RELATÓRIO.

    const handleRelatorio = useCallback(() => {
        try {
            setLoading(true);
            setOpenRelatorioModal(true);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar relatório!"));
        } finally {
            setLoading(false);
        }
    }, [
        pushErrorNotification,
    ]);

    const handleRelatorioOpenState = useCallback((openState: boolean) => {
        setOpenRelatorioModal(openState);

    }, []);

    const handleFluxoCaixaReportSubmit = useCallback(async (form: FluxoCaixaForm) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (requiredMimeType) => {
                    return await getReportFluxoCaixaUseCase.execute(
                        requiredMimeType,
                        form.idsBancoPagamento ?? [],
                        form.idsCentroCusto ?? [],
                        form.statusPagamento ?? null,
                        form.statusRecebimento ?? null,
                        form.dataDe ?? null,
                        form.dataAte ?? null,
                        form.idFornecedor ?? null,
                        form.idCliente ?? null,

                    );
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório de fluxo de caixa!"));
        } finally {
            setLoading(false);
        }
    }, [
        getReportFluxoCaixaUseCase,
        pushErrorNotification,
        startReportViewerProcess,
    ]);


    //#endregion

    return {
        loading,
        errorMessage,
        dashboardFinanceiroInformation: dashboardFinanceiroInformation ?? null,
        dashboardFinanceiroChart: dashboardFinanceiroChart ?? [],
        cardIds,
        filtroAno,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlBanco: ddlBanco ?? [],
        ddlFornecedor: ddlFornecedor ?? [],
        ddlCliente: ddlCliente ?? [],
        reportViewerState,
        openRelatorioModal,
        docLista,
        pagination,

        handleDownloadFile,
        handlePageChange,
        handleRowsPerPageChange,
        handleRelatorioOpenState,
        handleFluxoCaixaReportSubmit,
        handleAnoChange,
        handleMesChange,
        handleReload,
        handleRelatorio,
        handleCardClick,
    };
}